import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { CapacityRowKind, CapacityRowsSort, CapacitySnapshotStatus } from "@repo/shared";

import {
  getLatestCapacitySnapshot,
  listCapacityDirectory,
  listCapacitySpaces,
  listCapacityUsageRows,
  startCapacityCollection,
} from "@/lib/api/capacity";

const ACTIVE_STATUSES: CapacitySnapshotStatus[] = ["pending", "processing"];

export function isCapacityActive(status: CapacitySnapshotStatus | undefined | null): boolean {
  return !!status && ACTIVE_STATUSES.includes(status);
}

// Server-side filters of a ranking tab. They are part of the query key because
// the row cursor encodes the sort it was issued under: a cursor carried across a
// filter/sort change is rejected by the API (400), so each combination has to be
// its own paging sequence.
export type CapacityRowFilters = { search?: string; space?: string; sort: CapacityRowsSort };

export const capacityKeys = {
  all: ["capacity"] as const,
  latest: (connectionId: string) => [...capacityKeys.all, "latest", connectionId] as const,
  // The kind filter is part of the key: each ranking is its own cursor
  // sequence and the tabs must not share a cache entry.
  rows: (snapshotId: string, kind?: CapacityRowKind, filters?: CapacityRowFilters) =>
    [...capacityKeys.all, "rows", snapshotId, kind ?? "all", filters ?? {}] as const,
  spaces: (snapshotId: string, kind: CapacityRowKind) =>
    [...capacityKeys.all, "spaces", snapshotId, kind] as const,
  // One level of the space drill-down; the prefix is part of the key so each
  // level of the tree gets its own cache entry.
  directories: (snapshotId: string, space: string, prefix: string) =>
    [...capacityKeys.all, "directories", snapshotId, space, prefix] as const,
};

// The connection's newest snapshot drives the whole screen; it polls while the
// collection is still running and stops once it settles.
export const latestCapacitySnapshotQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: capacityKeys.latest(connectionId),
    queryFn: () => getLatestCapacitySnapshot(connectionId),
    enabled: !!connectionId,
    refetchInterval: (query) => (isCapacityActive(query.state.data?.status) ? 5000 : false),
  });

// A finished snapshot is immutable, so pages are pulled on demand and never
// refetched.
export const capacityUsageRowsInfiniteQueryOptions = (
  snapshotId: string,
  kind: CapacityRowKind | undefined,
  filters: CapacityRowFilters,
) =>
  infiniteQueryOptions({
    queryKey: capacityKeys.rows(snapshotId, kind, filters),
    queryFn: ({ pageParam }) =>
      listCapacityUsageRows(snapshotId, {
        cursor: pageParam as string | undefined,
        limit: 100,
        kind,
        sort: filters.sort,
        search: filters.search || undefined,
        space: filters.space || undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!snapshotId,
  });

// Spaces of one ranking's population: the space filter's options and, for the
// datafile ranking, the first level of the per-space view. Immutable like the
// snapshot itself.
export const capacitySpacesQueryOptions = (snapshotId: string, kind: CapacityRowKind) =>
  queryOptions({
    queryKey: capacityKeys.spaces(snapshotId, kind),
    queryFn: () => listCapacitySpaces(snapshotId, kind),
    enabled: !!snapshotId,
    staleTime: Infinity,
  });

// One level of a space's directory tree, aggregated server-side, fetched once
// and then cached as the user walks back up the breadcrumb.
export const capacityDirectoriesQueryOptions = (
  snapshotId: string,
  space: string,
  prefix: string,
) =>
  queryOptions({
    queryKey: capacityKeys.directories(snapshotId, space, prefix),
    queryFn: () => listCapacityDirectory(snapshotId, { space, prefix }),
    enabled: !!snapshotId && !!space,
    staleTime: Infinity,
  });

export function useStartCapacityCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => startCapacityCollection(connectionId),
    onSuccess: ({ snapshot }) => {
      // Seed the latest-snapshot cache so the status strip appears immediately
      // and its polling picks up from there.
      queryClient.setQueryData(capacityKeys.latest(snapshot.connectionId), snapshot);
    },
  });
}
